import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import GpsFixedRoundedIcon from '@mui/icons-material/GpsFixedRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import StraightenRoundedIcon from '@mui/icons-material/StraightenRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { DetailField, LoadingState, PageHeader, PageLayout, SectionCard } from '../../components/ui';

const DEFAULT_CENTER = { lat: 24.608768, lng: 46.678016 };
const DEFAULT_ZOOM = 12;

const initialForm = {
  name: '',
  latitude: String(DEFAULT_CENTER.lat),
  longitude: String(DEFAULT_CENTER.lng),
  allowed_radius_in_meters: '',
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatCoordinate = (value) => {
  const numeric = toNumber(value);
  return numeric === null ? '-' : numeric.toFixed(6);
};

function FieldGroup({ icon, title, description, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        {icon}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ color: 'text.secondary', display: 'grid', placeItems: 'center', mt: 0.1 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 850, overflowWrap: 'anywhere' }}>
          {value || '-'}
        </Typography>
      </Box>
    </Stack>
  );
}

function ProjectForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isEditing = Boolean(id);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  const [form, setForm] = useState(initialForm);
  const [originalForm, setOriginalForm] = useState(initialForm);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [locating, setLocating] = useState(false);

  const latitudeNumber = toNumber(form.latitude);
  const longitudeNumber = toNumber(form.longitude);
  const radiusNumber = parseInt(form.allowed_radius_in_meters, 10);
  const mapCenter = {
    lat: latitudeNumber ?? DEFAULT_CENTER.lat,
    lng: longitudeNumber ?? DEFAULT_CENTER.lng,
  };
  const validCoordinates = latitudeNumber !== null && longitudeNumber !== null;

  const requiredStatus = useMemo(
    () => [
      { label: 'Project name', done: Boolean(form.name.trim()) },
      { label: 'Latitude', done: latitudeNumber !== null },
      { label: 'Longitude', done: longitudeNumber !== null },
      { label: 'Radius', done: Number.isFinite(radiusNumber) && radiusNumber > 0 },
    ],
    [form.name, latitudeNumber, longitudeNumber, radiusNumber]
  );

  const completionPct = Math.round((requiredStatus.filter((item) => item.done).length / requiredStatus.length) * 100);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.hr?.radius?.md || 2,
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.74 : 0.92),
    },
  };

  useEffect(() => {
    if (!isEditing) return;

    const fetchProject = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/projects/${id}`);
        const data = res.data || {};
        const nextForm = {
          name: data.name || '',
          latitude: String(data.latitude ?? DEFAULT_CENTER.lat),
          longitude: String(data.longitude ?? DEFAULT_CENTER.lng),
          allowed_radius_in_meters: String(data.allowed_radius_in_meters ?? ''),
        };
        setForm(nextForm);
        setOriginalForm(nextForm);
      } catch (fetchError) {
        console.error('Failed to fetch project:', fetchError);
        setError('Error loading project data.');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id, isEditing]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      return;
    }

    setLocating(true);
    setError('');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm((current) => ({
          ...current,
          latitude: String(position.coords.latitude),
          longitude: String(position.coords.longitude),
        }));
        setLocating(false);
      },
      (geoError) => {
        console.error('Error getting location', geoError);
        setError('Could not get your location.');
        setLocating(false);
      }
    );
  };

  const handleMarkerDragEnd = (event) => {
    if (!event?.latLng) return;
    setForm((current) => ({
      ...current,
      latitude: String(event.latLng.lat()),
      longitude: String(event.latLng.lng()),
    }));
  };

  const handleReset = () => {
    setError('');
    setForm(isEditing ? originalForm : initialForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.name.trim()) {
      setError('Project name is required.');
      return;
    }

    if (!validCoordinates) {
      setError('Latitude and longitude must be valid numbers.');
      return;
    }

    const radius = Number.isFinite(radiusNumber) && radiusNumber > 0 ? radiusNumber : null;
    const payload = {
      name: form.name.trim(),
      latitude: latitudeNumber,
      longitude: longitudeNumber,
      allowed_radius_in_meters: radius,
    };

    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/projects/${id}`, payload);
        toast.success('Project updated successfully!');
      } else {
        await api.post('/projects', payload);
        toast.success('Project created successfully!');
      }
      navigate('/admin/projects');
    } catch (submitError) {
      console.error('Error saving project:', submitError);
      setError(submitError.response?.data?.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        variant="dashboard"
        overline="Operations"
        title={isEditing ? `Update Project #${id}` : 'Create Project'}
        subtitle="Define project geofence coordinates and attendance radius used by mobile check-in validation."
        actions={[
          <Button
            key="back"
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate('/admin/projects')}
          >
            Back to Projects
          </Button>,
        ]}
      />

      {loading ? (
        <LoadingState rows={8} />
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
            gap: 1.5,
            alignItems: 'start',
          }}
        >
          <SectionCard
            tone="dashboard"
            title="Project Geofence"
            subheader="Set the project name, coordinates, and allowed radius for mobile attendance."
            contentSx={{ p: { xs: 1.35, md: 1.7 } }}
          >
            <Stack spacing={1.8}>
              {error ? <Alert severity="error">{error}</Alert> : null}

              <FieldGroup
                icon={<TravelExploreRoundedIcon color="primary" />}
                title="Project Identity"
                description="Use a clear project name so employees can select it quickly in attendance flows."
              >
                <TextField
                  label="Project Name"
                  value={form.name}
                  onChange={(event) => updateField('name', event.target.value)}
                  required
                  sx={fieldSx}
                />
              </FieldGroup>

              <FieldGroup
                icon={<PlaceRoundedIcon color="primary" />}
                title="Location and Radius"
                description="Drag the marker, type exact coordinates, or use the current browser location."
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                    gap: 1.2,
                  }}
                >
                  <TextField
                    label="Latitude"
                    value={form.latitude}
                    onChange={(event) => updateField('latitude', event.target.value)}
                    required
                    error={form.latitude !== '' && latitudeNumber === null}
                    sx={fieldSx}
                  />
                  <TextField
                    label="Longitude"
                    value={form.longitude}
                    onChange={(event) => updateField('longitude', event.target.value)}
                    required
                    error={form.longitude !== '' && longitudeNumber === null}
                    sx={fieldSx}
                  />
                  <TextField
                    label="Allowed Radius (meters)"
                    value={form.allowed_radius_in_meters}
                    onChange={(event) => updateField('allowed_radius_in_meters', event.target.value)}
                    type="number"
                    inputProps={{ min: 0 }}
                    sx={fieldSx}
                  />
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.2 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<MyLocationRoundedIcon />}
                    onClick={handleUseMyLocation}
                    disabled={locating}
                  >
                    {locating ? 'Finding location...' : 'Use My Current Location'}
                  </Button>
                  <Button
                    type="button"
                    variant="text"
                    startIcon={<RestartAltRoundedIcon />}
                    onClick={() => {
                      updateField('latitude', String(DEFAULT_CENTER.lat));
                      updateField('longitude', String(DEFAULT_CENTER.lng));
                    }}
                  >
                    Reset Map Center
                  </Button>
                </Stack>
              </FieldGroup>

              <Box
                sx={{
                  height: { xs: 320, md: 420 },
                  overflow: 'hidden',
                  borderRadius: theme.hr?.radius?.lg || 3,
                  border: '1px solid',
                  borderColor: theme.hr?.line?.soft || 'divider',
                  bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.55 : 0.78),
                }}
              >
                {googleMapsApiKey ? (
                  <APIProvider apiKey={googleMapsApiKey}>
                    <Map
                      style={{ width: '100%', height: '100%' }}
                      center={mapCenter}
                      defaultCenter={DEFAULT_CENTER}
                      defaultZoom={DEFAULT_ZOOM}
                      gestureHandling="greedy"
                    >
                      <Marker
                        position={mapCenter}
                        label="Project"
                        draggable
                        onDragEnd={handleMarkerDragEnd}
                      />
                    </Map>
                  </APIProvider>
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'grid',
                      placeItems: 'center',
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    <Alert severity="warning" sx={{ maxWidth: 520 }}>
                      Google Maps API key is missing or invalid. You can still save coordinates manually.
                    </Alert>
                  </Box>
                )}
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={handleReset}
                  disabled={saving}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveRoundedIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : isEditing ? 'Update Project' : 'Save Project'}
                </Button>
              </Stack>
            </Stack>
          </SectionCard>

          <Stack spacing={1.5} sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
            <SectionCard
              tone="dashboard"
              title="Geofence Summary"
              subheader="Live preview of the location configuration."
              contentSx={{ p: 1.6 }}
            >
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={completionPct === 100 ? 'success' : 'warning'}
                    label={`${completionPct}% complete`}
                  />
                  <Chip size="small" variant="outlined" label={isEditing ? 'Edit mode' : 'Create mode'} />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={completionPct}
                  sx={{ height: 8, borderRadius: 999, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                />
                <SummaryRow
                  icon={<TravelExploreRoundedIcon fontSize="small" />}
                  label="Project"
                  value={form.name}
                />
                <SummaryRow
                  icon={<GpsFixedRoundedIcon fontSize="small" />}
                  label="Latitude"
                  value={formatCoordinate(form.latitude)}
                />
                <SummaryRow
                  icon={<GpsFixedRoundedIcon fontSize="small" />}
                  label="Longitude"
                  value={formatCoordinate(form.longitude)}
                />
                <SummaryRow
                  icon={<StraightenRoundedIcon fontSize="small" />}
                  label="Allowed Radius"
                  value={radiusNumber > 0 ? `${radiusNumber} meters` : '-'}
                />
                <DetailField
                  compact
                  label="Map Status"
                  value={googleMapsApiKey ? 'Map preview enabled' : 'Manual coordinates only'}
                  icon={<MapRoundedIcon fontSize="small" />}
                />
              </Stack>
            </SectionCard>
          </Stack>
        </Box>
      )}
    </PageLayout>
  );
}

export default ProjectForm;
